/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useMemo } from 'react';

import { cssMap, jsx } from '@atlaskit/css';
import FileIcon from '@atlaskit/icon/core/file';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import { Text } from '@atlaskit/primitives/compiled/text';
import { Card as SmartCard } from '@atlaskit/smart-card/card/lazy';
import { SnippetBlock } from '@atlaskit/smart-card/snippet-block';
import { TitleBlock } from '@atlaskit/smart-card/title-block';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';

import { type NLPSearchResultSource } from '../../../ai-answer-dialog/types';
import { createFallbackComponent, type OnClickSource } from './create-fallback-component';
import { SourcesPopupPresentation } from './SourcesPopupPresentation';
import { extractReadableTitleFromUrl } from './url-transformer/extract-readable-title-from-url';
import { resolvesToReadableTitle } from './url-transformer/resolves-to-readable-title';
import { shouldSkipSmartCardResolution } from './url-transformer/should-skip-smart-card-resolution';
import { stripSlackQueryParams } from './url-transformer/strip-slack-query-params';
import { transformIfConfluenceLink } from './url-transformer/transform-if-confluence-link';

const styles = cssMap({
	sourceCard: {
		border: 'none',
		textAlign: 'left',
	},
	sourceListItem: {
		borderRadius: token('radius.medium'),
		paddingTop: token('space.100'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.100'),
		cursor: 'pointer',
		backgroundColor: 'transparent',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- match ai-mate source styling
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		transition: token('motion.button.hovered'),
	},
	pdfLink: {
		display: 'flex',
		cursor: 'pointer',
		paddingLeft: token('space.0'),
		paddingRight: token('space.0'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- match ai-mate source styling
		'&:hover': {
			textDecoration: 'underline',
		},
	},
});

export type SourcesPopupContentProps = {
	sources: NLPSearchResultSource[];
	onClickSourceHandler: (
		type: string,
		ari?: string,
		product?: string | null,
		index?: number,
	) => void;
	onClickSource?: OnClickSource;
	onClose: () => void;
	openSourceLinkInSameTab?: boolean;
};

type SourceSmartCardProps = {
	fallbackComponent: React.ComponentType;
	url: string;
};

const SourceSmartCard = ({ fallbackComponent, url }: SourceSmartCardProps): JSX.Element => {
	return (
		<SmartCard
			appearance="block"
			url={url}
			showHoverPreview={false}
			fallbackComponent={fallbackComponent}
			ui={{
				hideBackground: true,
				hideElevation: true,
				hidePadding: true,
			}}
		>
			<TitleBlock maxLines={1} />
			<SnippetBlock maxLines={2} />
		</SmartCard>
	);
};

export const SourcesPopupContent = ({
	sources,
	onClickSourceHandler,
	onClickSource,
	onClose,
	openSourceLinkInSameTab = false,
}: SourcesPopupContentProps): JSX.Element => {
	const sourcesList = useMemo(() => {
		return sources.map((source, index) => {
			const { url, title, type, ari, product } = source;
			if (!url) {
				return null;
			}

			const processedUrl = stripSlackQueryParams(url);
			const transformedUrl = processedUrl
				? transformIfConfluenceLink(processedUrl, true)
				: processedUrl;
			const originalTransformedUrl = transformIfConfluenceLink(url, true);

			const hasReadableTitle = resolvesToReadableTitle(url, title);
			const displayTitle = hasReadableTitle ? title : extractReadableTitleFromUrl(url);

			const skipResolution = shouldSkipSmartCardResolution(url);

			const handleItemClick = (e: React.MouseEvent) => {
				onClickSourceHandler(type, ari, product, index);
				e.preventDefault();
				window.open(
					originalTransformedUrl,
					openSourceLinkInSameTab ? '_self' : '_blank',
					'noopener, noreferrer',
				);
			};

			const handleLiClick = () => {
				onClickSourceHandler(type, ari, product, index);
			};

			const handlePressableClick = (e: React.MouseEvent) => {
				e.stopPropagation();
				handleItemClick(e);
			};

			const handleLiKeyDown = (e: React.KeyboardEvent) => {
				if (e.key === 'Enter' || e.key === ' ') {
					onClickSourceHandler(type, ari, product, index);
				}
			};

			return (
				<li
					key={index}
					onClick={skipResolution ? undefined : handleLiClick}
					onKeyDown={skipResolution ? undefined : handleLiKeyDown}
				>
					{skipResolution ? (
						<Pressable onClick={handlePressableClick} xcss={styles.sourceListItem}>
							<Tooltip content={displayTitle}>
								<Box xcss={styles.pdfLink}>
									<Inline space="space.075" alignBlock="center">
										<FileIcon size="medium" label="" />
										<Text size="medium" weight="regular" maxLines={1} color="color.link">
											{displayTitle}
										</Text>
									</Inline>
								</Box>
							</Tooltip>
						</Pressable>
					) : (
						<Box xcss={styles.sourceListItem}>
							<Box xcss={styles.sourceCard}>
								<SourceSmartCard
									key={transformedUrl}
									url={transformedUrl}
									fallbackComponent={createFallbackComponent(
										url,
										type,
										displayTitle,
										ari,
										product,
										onClickSource,
										true,
										index,
									)}
								/>
							</Box>
						</Box>
					)}
				</li>
			);
		});
	}, [onClickSource, onClickSourceHandler, openSourceLinkInSameTab, sources]);

	return (
		<SourcesPopupPresentation sourceCount={sources.length} onClose={onClose}>
			{sourcesList}
		</SourcesPopupPresentation>
	);
};
